import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import MarkdownMessage from '../../components/chat/MarkdownMessage';
import { wikiApi, type WikiPageView } from '../../services/wiki-api';
import { handleApiError } from '../../services/api';

/** Raw-markdown editor; a save becomes a new revision attributed to the human. */
const EditView: React.FC<{ page: WikiPageView; onDone: () => void }> = ({ page, onDone }) => {
  const queryClient = useQueryClient();
  const [content, setContent] = useState(page.content);
  const [summary, setSummary] = useState('');
  const [preview, setPreview] = useState(false);

  const save = useMutation({
    mutationFn: () => wikiApi.updatePage(page.id, content, summary || undefined),
    onSuccess: ({ version }) => {
      toast.success(`Saved as v${version}`);
      queryClient.invalidateQueries({ queryKey: ['wikiPage', page.id] });
      queryClient.invalidateQueries({ queryKey: ['wikiRevisions', page.id] });
      queryClient.invalidateQueries({ queryKey: ['wikiTree'] });
      onDone();
    },
    onError: (err) => toast.error(handleApiError(err)),
  });

  return (
    <div className="px-5 py-4">
      <p className="text-[11.5px] text-muted-foreground mb-2.5">
        Editing the markdown directly. Your save becomes{' '}
        <b className="text-foreground">v{page.version + 1}, attributed to you</b> — the AI keeps
        maintaining the page afterwards but treats human edits as authoritative.
      </p>

      {preview ? (
        <div className="min-h-[340px] max-h-[60vh] overflow-y-auto rounded-[10px] border border-input bg-background px-4 py-3">
          <MarkdownMessage content={content} />
        </div>
      ) : (
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          spellCheck={false}
          className="w-full min-h-[340px] max-h-[60vh] rounded-[10px] border border-input bg-background px-4 py-3 font-mono text-[11.5px] leading-relaxed text-foreground resize-y focus:outline-none focus:ring-1 focus:ring-ring"
        />
      )}

      <input
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        placeholder="Change summary (optional) — shown in History"
        maxLength={500}
        className="w-full mt-2.5 rounded-[10px] border border-input bg-background px-3 py-2 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      />

      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          disabled={save.isPending || content === page.content}
          onClick={() => save.mutate()}
          className="rounded-[9px] bg-primary text-primary-foreground px-4 py-1.5 text-[12.5px] font-semibold disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : `Save as v${page.version + 1}`}
        </button>
        <button
          type="button"
          onClick={() => setPreview((p) => !p)}
          className="rounded-[9px] border border-input bg-card px-4 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-accent"
        >
          {preview ? 'Edit' : 'Preview'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded-[9px] border border-input bg-card px-4 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-accent"
        >
          Cancel
        </button>
        <span className="ml-auto text-[11px] text-muted-foreground">Markdown</span>
      </div>
    </div>
  );
};

export default EditView;
